import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { baseApiSlice } from '../commons/base-api.config';
import persistReducer from 'redux-persist/es/persistReducer';
import { APP_ENVIRONMENT_PRODUCTION } from '../constants/app';
import config from '../config';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { commonReducer } from './slice/common.slice';
import { editFlowReducer } from './slice/editor.slice';
import { userReducer } from './slice/user.slice';
import { authReducer } from './slice/auth.slice';
import { brandReducer } from './slice/brand.slice';
import { organizationReducer } from './slice/organization.slice';
import { assetsReducer } from './slice/assets.slice';
import { brandElementsReducer } from './slice/brand-elements.slice';
import { campaignReducer } from './slice/campaign.slice';
import { templateReducer } from './slice/template.slice';
import { sessionReducer } from './slice/session.slice';
import { campaignAdsReducer } from './slice/campaign-ads.slice';
import { adFromUrlReducer } from './slice/ad-from-url.slice';

const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['user'],
};
const combinedMiddleware = (getDefaultMiddleware: CurriedGetDefaultMiddleware) =>
    getDefaultMiddleware({
        serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
    }).concat([baseApiSlice.middleware]);

const combinedReducer = combineReducers({
    common: commonReducer,
    editFlow: editFlowReducer,
    user: userReducer,
    auth: authReducer,
    brand: brandReducer,
    organization: organizationReducer,
    brandElements: brandElementsReducer,
    assets: assetsReducer,
    campaign: campaignReducer,
    template: templateReducer,
    session: sessionReducer,
    campaignAds: campaignAdsReducer,
    adFromUrl: adFromUrlReducer,
    [baseApiSlice.reducerPath]: baseApiSlice.reducer,
});
const persistedReducers = persistReducer(persistConfig, combinedReducer);

export const store = configureStore({
    reducer: persistedReducers,
    middleware: combinedMiddleware,
    devTools: config.env !== APP_ENVIRONMENT_PRODUCTION,
});

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof persistedReducers>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
